// src/components/HomePageInvoiceProfiles.tsx
import React, { useMemo } from 'react';
import SectionCard from './SectionCard';
import {
  CreditCardOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Empty,
  Flex,
  Skeleton,
  Space,
  Tag,
  Typography,
  theme,
} from 'antd';
import { useLanguage } from 'src/providers/LanguageProvider';
import { IInvoiceProfile } from 'src/types/types';
import { useNavigate } from 'react-router';
import useFormatDate from 'src/hooks/useFormatDate';

const { Text } = Typography;

type Props = {
  profiles: IInvoiceProfile[];
  loading?: boolean;
  error?: string | null;
  selectedProfileId?: number | null;
  onSelectProfile?: (id: number) => void;
  maxVisible?: number;      // anasayfada kaç tane gösterilsin (default: 3)
  onViewAll?: () => void;   // "Tümünü gör" tıklaması
};

/* —— Helpers —— */
function initials(name?: string) {
  if (!name) return '•';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]?.toUpperCase() || '').join('') || '•';
}

function maskIban(iban?: string) {
  if (!iban) return '-';
  const clean = iban.replace(/\s+/g, '');
  if (clean.length <= 10) return clean; // kısa IBAN'ı maskeleme
  const head = clean.slice(0, 6);
  const tail = clean.slice(-4);
  const masked = '•'.repeat(Math.max(0, clean.length - head.length - tail.length));
  // 4'lü gruplara bölerek okunabilir yazalım
  const grouped = (head + masked + tail).match(/.{1,4}/g)?.join(' ') || head + masked + tail;
  return grouped;
}

// function formatDate(iso?: string, locale = 'tr-TR') {
//   if (!iso) return '-';
//   const d = new Date(iso);
//   if (Number.isNaN(d.getTime())) return '-';
//   return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: '2-digit' });
// }

const HomePageInvoiceProfiles: React.FC<Props> = ({
  profiles,
  loading = false,
  error = null,
  selectedProfileId = null,
  onSelectProfile,
  maxVisible = 3,
  onViewAll,
}) => {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  const items = useMemo(() => profiles ?? [], [profiles]);
  const shown = items.slice(0, maxVisible);
  const remaining = Math.max(0, items.length - shown.length);

  const navigate = useNavigate();

  const formatDate = useFormatDate()

  return (
    <SectionCard
      icon={<CreditCardOutlined />}
      title={t('INVOICE_PROFILES', 'Fatura Profilleri')}
      extra={
        <Button type="text" size="small" onClick={() => navigate('/invoice-profiles')}>
          {t('VIEW_ALL')}
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Flex align="start" gap={8}>
          <Text type="secondary">
            {t(
              'INVOICE_PROFILES_DESC')}
          </Text>
        </Flex>

        {error ? (
          <Alert type="error" showIcon message={t('ERROR', 'Hata')} description={error} />
        ) : loading ? (
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Skeleton active avatar paragraph={{ rows: 1 }} />
            <Skeleton active avatar paragraph={{ rows: 1 }} />
            <Skeleton active avatar paragraph={{ rows: 1 }} />
          </Space>
        ) : items.length === 0 ? (
          <Empty
            description={t('NO_PROFILES', 'Kayıtlı fatura profiliniz yok')}
            imageStyle={{ height: 60 }}
            style={{ marginBlock: 8 }}
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            {shown.map((p) => {
              const selected = p.Id === selectedProfileId;
              const statusColor = p.IsActive ? 'success' : 'default';

              return (
                <Flex
                  key={p.Id}
                  align="center"
                  justify="space-between"
                  gap={12}
                  style={{
                    width: '100%',
                    padding: 10,
                    borderRadius: 10,
                    border: `1px solid ${selected ? token.colorPrimary : token.colorSplit}`,
                    background: token.colorBgContainer,
                    boxShadow: selected ? `0 0 0 2px ${token.colorPrimaryBorder}` : undefined,
                    cursor: onSelectProfile ? 'pointer' : 'default',
                  }}
                  onClick={() => onSelectProfile?.(p.Id)}
                >
                  {/* Sol: Avatar + başlık + meta */}
                  <Flex align="center" gap={12} style={{ minWidth: 0 }}>
                    <Badge dot color={statusColor} offset={[-2, 30]}>
                      <Avatar shape="square" size={40}>
                        {initials(p.CompanyName)}
                      </Avatar>
                    </Badge>

                    <div style={{ minWidth: 0 }}>
                      <Flex align="center" gap={8} wrap>
                        <Text strong ellipsis style={{ display: 'block', maxWidth: 320 }}>
                          {p.CompanyName}
                        </Text>
                        {selected && (
                          <Tag color="processing" bordered={false} icon={<CheckCircleFilled />}>
                            {t('SELECTED', 'Seçili')}
                          </Tag>
                        )}
                        <Tag color={statusColor} bordered={false}>
                          {p.IsActive ? t('ACTIVE', 'Aktif') : t('INACTIVE', 'Pasif')}
                        </Tag>
                      </Flex>

                      <Flex gap={12} wrap>
                        {/* Email */}
                        {p.Email && (
                          <Space size={6}>
                            <MailOutlined />
                            <a href={`mailto:${p.Email}`} style={{ color: token.colorTextSecondary }}>
                              {p.Email}
                            </a>
                          </Space>
                        )}

                        {/* Konum */}
                        {(p.CompanyCity || p.CompanyCountry) && (
                          <Space size={6}>
                            <EnvironmentOutlined />
                            <Text type="secondary">
                              {[p.CompanyCity, p.CompanyCountry].filter(Boolean).join(', ')}
                            </Text>
                          </Space>
                        )}
                      </Flex>
                    </div>
                  </Flex>

                  {/* Sağ: IBAN + tarih */}
                  <Flex vertical align="end" gap={4} style={{ textAlign: 'right' }}>
                    <Tag bordered={false} style={{ margin: 0 }}>
                      {t('IBAN')}: {maskIban(p.IBAN)}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {/* {t('UPDATED_AT')}: {formatDate(p.UpdatedAt)} */}
                      {p?.UpdatedAt ? `${t('UPDATED_AT')}: ${formatDate(p.UpdatedAt)}` : p?.CreatedAt ? `${t('CREATED_AT')}: ${formatDate(p.CreatedAt)}` : ''}
                    </Text>
                  </Flex>
                </Flex>
              );
            })}

            {remaining > 0 && (
              <Text type="secondary">
                {remaining > 0 && (
                  <Text type="secondary">
                    {t('AND_MORE_PROFILES', `${remaining} more invoice profiles`)}
                  </Text>
                )}

              </Text>
            )}
          </Space>
        )}
      </Space>
    </SectionCard>
  );
};

export default HomePageInvoiceProfiles;
